import { useApolloClient } from "@apollo/client";
import { ModalOpenButton } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { ConfirmModal } from "../../../../commons/modals/ConfirmModal.js";
import { namedOperations } from "../../../../../clients/graphqlTypes.js";
import { useDiscardUnpublishedChangesMutation } from "./QuoteTemplateDiscardUnpublishedChangesButton.generated.js";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import { Button, ButtonProps } from "@mui/material";

interface Props {
  pathToDoc?: string;
  docId: string;
  buttonProps?: ButtonProps;
  buttonTitle?: string;
}

export function QuoteTemplateDiscardUnpublishedChangesButton({
  docId,
  pathToDoc,
  buttonProps,
  buttonTitle,
}: Props) {
  const { t } = useTranslate(["Templates"]);
  return (
    <ModalOpenButton
      Modal={QuoteTemplateDiscardUnpublishedChangesModal}
      modalProps={{ docId, pathToDoc }}
    >
      <Button
        variant="outlined"
        startIcon={<SettingsBackupRestoreIcon />}
        {...buttonProps}
      >
        {buttonTitle ??
          t("Revert to last published version", { ns: "Templates" })}
      </Button>
    </ModalOpenButton>
  );
}

export const QuoteTemplateDiscardUnpublishedChangesModal = ({
  handleClose,
  docId,
  pathToDoc,
}: {
  handleClose: () => void;
  pathToDoc?: string;
  docId: string;
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Templates"]);

  const client = useApolloClient();
  const [discard] = useDiscardUnpublishedChangesMutation({
    client,
    variables: { input: { docId } },
    refetchQueries: [
      namedOperations.Query.TemplateQuoteRoutes,
      namedOperations.Query.TemplateQuoteEdit,
      namedOperations.Query.TemplateQuoteEditPreview,
      namedOperations.Query.TemplatesQuoteTemplateManageVersionsModal,
    ],
    awaitRefetchQueries: true,
  });

  const discardUnpublishedChanges = async () => {
    await discard();
    enqueueSnackbar(t("Unpublished changes discarded", { ns: "Templates" }), {
      variant: "success",
    });
    if (pathToDoc) {
      navigate(`${pathToDoc}/latest`);
    }
  };

  return (
    <ConfirmModal
      handleClose={handleClose}
      text={t(
        "You will lose all unpublished changes made since this template was last published",
        { ns: "Templates" }
      )}
      confirmButtonLabel={t("Yes, revert changes", { ns: "Templates" })}
      handleConfirm={discardUnpublishedChanges}
    />
  );
};
